import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { API_URL } from '../lib/api';
import useRealtimeEvents from '../hooks/useRealtimeEvents';

export const SITE_KILLSWITCH_ADMIN_EMAIL = 'admin@camsurpickleball.com';

const SiteKillswitchContext = createContext(null);

const isAllowedFrozenTarget = (target) => {
  if (!target || typeof target.closest !== 'function') return false;
  if (target.closest('[data-site-killswitch]')) return true;
  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/admin/login')) {
    return true;
  }
  return false;
};

export function SiteKillswitchProvider({ children }) {
  const [live, setLiveState] = useState(true);

  const refreshStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/site/status`);
      if (!res.ok) return;
      const data = await res.json();
      if (typeof data?.live === 'boolean') setLiveState(data.live);
    } catch {
      /* keep last known state */
    }
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useRealtimeEvents(['site_killswitch'], (msg) => {
    if (typeof msg?.live === 'boolean') setLiveState(msg.live);
  });

  const setLive = useCallback(async (nextLive) => {
    const token = localStorage.getItem('admin_token');
    const res = await fetch(`${API_URL}/admin/site/killswitch`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ live: Boolean(nextLive) }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = typeof data.detail === 'string' ? data.detail : 'Could not update site killswitch';
      throw new Error(detail);
    }
    if (typeof data.live === 'boolean') setLiveState(data.live);
    return data.live;
  }, []);

  useEffect(() => {
    if (live) return undefined;
    const block = (event) => {
      if (isAllowedFrozenTarget(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    };
    const types = [
      'click',
      'pointerdown',
      'pointerup',
      'mousedown',
      'mouseup',
      'touchstart',
      'touchend',
      'submit',
      'keydown',
      'keyup',
    ];
    types.forEach((type) => document.addEventListener(type, block, true));
    return () => {
      types.forEach((type) => document.removeEventListener(type, block, true));
    };
  }, [live]);

  const value = useMemo(
    () => ({ live, setLive, refreshStatus }),
    [live, setLive, refreshStatus],
  );

  return (
    <SiteKillswitchContext.Provider value={value}>
      {children}
      {!live ? (
        <div
          className="pointer-events-none fixed inset-x-0 top-0 z-[9997] flex justify-center px-3 pt-3"
          aria-live="polite"
        >

        </div>
      ) : null}
    </SiteKillswitchContext.Provider>
  );
}

export function useSiteKillswitch() {
  const ctx = useContext(SiteKillswitchContext);
  if (!ctx) {
    throw new Error('useSiteKillswitch must be used within SiteKillswitchProvider');
  }
  return ctx;
}
